import styled, { css, keyframes } from 'styled-components'

import { Icon } from 'ui/styled'

const launcherReveal = keyframes`
  from { opacity: 0; transform: translateY(14px) scale(0.92); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`

const panelReveal = keyframes`
  from { opacity: 0; transform: translateY(16px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`

const introReveal = keyframes`
  from { opacity: 0; transform: translateX(8px); }
  to { opacity: 1; transform: translateX(0); }
`

const typingPulse = keyframes`
  0%, 60%, 100% { opacity: 0.34; transform: translateY(0); }
  30% { opacity: 1; transform: translateY(-3px); }
`

export const AssistantLayer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1500;
  pointer-events: ${p => p.$open ? 'auto' : 'none'};

  @media (max-width: 640px) {
    background: ${p => p.$open ? p.theme.palette.colors.backdrop : p.theme.palette.colors.backgroundTransparent};
    pointer-events: ${p => p.$open ? 'auto' : 'none'};
  }
`

export const LauncherPosition = styled.div`
  position: fixed;
  right: 24px;
  bottom: calc(24px + env(safe-area-inset-bottom));
  display: ${p => p.$hidden ? 'none' : 'flex'};
  align-items: flex-end;
  gap: 12px;
  pointer-events: auto;
  animation: ${launcherReveal} 0.36s cubic-bezier(0.22, 1, 0.36, 1) both;

  @media (max-width: 640px) {
    display: ${p => p.$hidden || p.$open ? 'none' : 'flex'};
    right: 16px;
    bottom: calc(16px + env(safe-area-inset-bottom));
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

export const IntroBubble = styled.div`
  position: relative;
  width: min(220px, calc(100vw - 116px));
  padding: 13px 38px 13px 15px;
  border: 1px solid ${p => p.theme.palette.colors.borderSoft};
  border-radius: 18px 18px 5px 18px;
  background: ${p => p.theme.palette.colors.surfaceElevated};
  box-shadow: 0 16px 40px ${p => p.theme.palette.colors.shadowStrong};
  color: ${p => p.theme.palette.colors.text};
  animation: ${introReveal} 0.28s ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

export const IntroTitle = styled.strong`
  display: block;
  margin-bottom: 2px;
  font-family: Gilroy, sans-serif;
  font-size: 14px;
  line-height: 1.2;
`

export const IntroText = styled.span`
  display: block;
  color: ${p => p.theme.palette.colors.muted};
  font-family: Lato, sans-serif;
  font-size: 12px;
  line-height: 1.35;
`

export const IntroClose = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 26px;
  height: 26px;
  border: 0;
  border-radius: 50%;
  background: ${p => p.theme.palette.colors.buttonGhost};
  color: ${p => p.theme.palette.colors.muted};
  font: 700 18px/1 Lato, sans-serif;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${p => p.theme.palette.blue.main};
    outline-offset: 2px;
  }
`

export const LauncherButton = styled.button`
  position: relative;
  width: 92px;
  height: 92px;
  flex: 0 0 92px;
  display: grid;
  place-items: center;
  padding: 0;
  border: 0;
  background: transparent;
  filter: drop-shadow(0 14px 18px ${p => p.theme.palette.colors.shadowLift});
  cursor: pointer;
  transition: transform 0.2s ease, filter 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    filter:
      drop-shadow(0 18px 22px ${p => p.theme.palette.colors.shadowLift})
      drop-shadow(0 0 8px ${p => p.theme.palette.blue.main});
  }

  &:active {
    transform: translateY(0) scale(0.97);
  }

  &:focus-visible {
    outline: 3px solid ${p => p.theme.palette.blue.main};
    outline-offset: 4px;
  }

  @media (max-width: 640px) {
    width: 76px;
    height: 76px;
    flex-basis: 76px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const LauncherImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`

export const AvatarFallback = styled.span`
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  border-radius: inherit;
  background: ${p => p.theme.palette.blue.main};
  color: ${p => p.theme.palette.blue.contrastText};
  font-family: Gilroy, sans-serif;
  font-size: ${p => p.$small ? '18px' : '30px'};
  font-weight: 800;
`

export const AvailabilityDot = styled.span`
  position: absolute;
  right: 2px;
  bottom: 6px;
  width: 17px;
  height: 17px;
  border: 4px solid ${p => p.theme.palette.colors.surfaceElevatedStrong};
  border-radius: 50%;
  background: ${p => p.theme.palette.success.main};
`

export const Panel = styled.section`
  position: fixed;
  right: 24px;
  bottom: calc(132px + env(safe-area-inset-bottom));
  width: min(404px, calc(100vw - 48px));
  height: min(640px, calc(100dvh - 164px));
  min-height: 430px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid ${p => p.theme.palette.colors.borderStrong};
  border-radius: 28px;
  background: ${p => p.theme.palette.colors.backgroundStrong};
  box-shadow: 0 28px 80px ${p => p.theme.palette.colors.shadowLift};
  color: ${p => p.theme.palette.colors.text};
  pointer-events: auto;
  animation: ${panelReveal} 0.26s cubic-bezier(0.22, 1, 0.36, 1) both;

  &:focus {
    outline: none;
  }

  @media (max-width: 640px) {
    inset: 0;
    width: 100%;
    height: 100dvh;
    min-height: 0;
    max-height: none;
    border: 0;
    border-radius: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

export const PanelLoading = styled(Panel)`
  min-height: 180px;
  align-items: center;
  justify-content: center;
  color: ${p => p.theme.palette.colors.muted};
  font-family: Lato, sans-serif;
  font-size: 14px;
`

export const ChatHeader = styled.header`
  min-height: 84px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  border-bottom: 1px solid ${p => p.theme.palette.colors.borderSoft};
  background:
    radial-gradient(circle at 12% 10%, ${p => p.theme.palette.colors.shellGradientStart} 0%, transparent 58%),
    ${p => p.theme.palette.colors.surfaceElevatedSoft};

  @media (max-width: 640px) {
    padding: calc(14px + env(safe-area-inset-top)) 16px 14px;
  }
`

export const HeaderAvatar = styled.div`
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  overflow: hidden;
  border: 1px solid ${p => p.theme.palette.blue.main};
  border-radius: 17px;
  background: ${p => p.theme.palette.colors.surfaceElevatedStrong};
  box-shadow: 0 0 0 4px ${p => p.theme.palette.colors.shellGlow};
`

export const HeaderAvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

export const HeaderCopy = styled.div`
  min-width: 0;
  flex: 1;
`

export const HeaderTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const HeaderTitle = styled.h2`
  margin: 0;
  font-family: Gilroy, sans-serif;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.1;
`

export const PreviewBadge = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  padding: 2px 7px;
  border-radius: 999px;
  background: ${p => p.theme.palette.colors.badgeMuted};
  color: ${p => p.theme.palette.blue.main};
  font-family: Poppins, sans-serif;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`

export const HeaderSubtitle = styled.p`
  margin: 4px 0 0;
  overflow: hidden;
  color: ${p => p.theme.palette.colors.muted};
  font-family: Lato, sans-serif;
  font-size: 13px;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const CloseButton = styled.button`
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  display: grid;
  place-items: center;
  border: 1px solid ${p => p.theme.palette.colors.borderSoft};
  border-radius: 14px;
  background: ${p => p.theme.palette.colors.buttonGhost};
  color: ${p => p.theme.palette.colors.text};
  font: 400 25px/1 Lato, sans-serif;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: ${p => p.theme.palette.colors.badgeMuted};
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid ${p => p.theme.palette.blue.main};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const MessageScroller = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 20px 18px 14px;
  scrollbar-color: ${p => p.theme.palette.blue.main} ${p => p.theme.palette.colors.backgroundTransparent};
  scrollbar-width: thin;

  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-track { background: ${p => p.theme.palette.colors.backgroundTransparent}; }
  &::-webkit-scrollbar-thumb { border-radius: 999px; background: ${p => p.theme.palette.blue.main}; }
`

export const MessageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const MessageRow = styled.article`
  display: flex;
  justify-content: ${p => p.$user ? 'flex-end' : 'flex-start'};
`

export const MessageBubble = styled.div`
  max-width: 86%;
  padding: 12px 14px;
  border: 1px solid ${p => p.$user ? p.theme.palette.blue.main : p.theme.palette.colors.borderSoft};
  border-radius: ${p => p.$user ? '18px 18px 5px 18px' : '18px 18px 18px 5px'};
  background: ${p => p.$user ? p.theme.palette.blue.main : p.theme.palette.colors.surfaceSoft};
  color: ${p => p.$user ? p.theme.palette.blue.contrastText : p.theme.palette.colors.text};
  font-family: Lato, sans-serif;
  font-size: 16px;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`

export const CitationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid ${p => p.theme.palette.colors.borderSoft};
`

export const CitationTitle = styled.strong`
  color: ${p => p.theme.palette.colors.subdued};
  font-family: Poppins, sans-serif;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`

export const DiscoveryFollowUp = styled.p`
  margin: 12px 0 0;
`

export const CitationLink = styled.a`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 7px 8px;
  border-radius: 9px;
  background: ${p => p.theme.palette.colors.buttonGhost};
  color: ${p => p.theme.palette.colors.text};
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  text-decoration: none;

  &:hover { background: ${p => p.theme.palette.colors.badgeMuted}; }
  &:focus-visible { outline: 2px solid ${p => p.theme.palette.blue.main}; }
`

export const CitationMeta = styled.span`
  flex: 0 0 auto;
  color: ${p => p.theme.palette.colors.subdued};
  font-size: 10px;
  font-weight: 600;
`

export const FeedbackRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;
  margin-top: 10px;
`

export const FeedbackLabel = styled.span`
  color: ${p => p.theme.palette.colors.subdued};
  font-size: 12px;
`

export const FeedbackButton = styled.button`
  padding: 3px 5px;
  border: 0;
  border-radius: 7px;
  background: ${p => p.theme.palette.colors.buttonGhost};
  font-size: 16px;
  line-height: 1;
  cursor: pointer;

  &:focus-visible { outline: 2px solid ${p => p.theme.palette.blue.main}; }
`

export const FeedbackReasons = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`

export const FeedbackReasonButton = styled.button`
  padding: ${p => p.$entry ? '8px 14px' : '5px 7px'};
  min-height: ${p => p.$entry ? '44px' : 'auto'};
  margin-left: ${p => p.$entry ? '14px' : '0'};
  border: 1px solid ${p => p.theme.palette.colors.borderSoft};
  border-radius: 999px;
  background: ${p => p.theme.palette.colors.buttonGhost};
  color: ${p => p.theme.palette.colors.text};
  font-family: Lato, sans-serif;
  font-size: ${p => p.$entry ? '13px' : '11px'};
  cursor: pointer;

  &:hover { border-color: ${p => p.theme.palette.blue.main}; }
  &:focus-visible { outline: 2px solid ${p => p.theme.palette.blue.main}; }
`

export const QuickActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  margin-top: 18px;
`

export const ShareFeedbackButton = styled(FeedbackReasonButton)`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: ${p => p.$entry ? '0' : '10px'};
  min-height: 40px;
  padding: 8px 12px;
  border-radius: 10px;
  border-color: ${p => p.theme.palette.colors.borderStrong};
  font-size: 12px;
  font-weight: 700;
  &:disabled { opacity: .5; cursor: not-allowed; }
`

export const QuickActionsLabel = styled.p`
  margin: 0 0 1px;
  color: ${p => p.theme.palette.colors.subdued};
  font-family: Poppins, sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`

export const QuickActionButton = styled.button`
  min-height: 38px;
  padding: 9px 13px;
  border: 1px solid ${p => p.theme.palette.colors.borderSoft};
  border-radius: 999px;
  background: ${p => p.theme.palette.colors.buttonGhost};
  color: ${p => p.theme.palette.colors.text};
  font-family: Lato, sans-serif;
  font-size: 14px;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;

  &:hover:not(:disabled) {
    border-color: ${p => p.theme.palette.blue.main};
    background: ${p => p.theme.palette.colors.badgeMuted};
    transform: translateX(2px);
  }

  &:focus-visible {
    outline: 2px solid ${p => p.theme.palette.blue.main};
    outline-offset: 2px;
  }

  &:disabled {
    color: ${p => p.theme.palette.colors.disabledText};
    cursor: not-allowed;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const TypingBubble = styled(MessageBubble)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 54px;
  min-height: 38px;
`

export const TypingDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${p => p.theme.palette.blue.main};
  animation: ${typingPulse} 1.1s ease-in-out infinite;
  animation-delay: ${p => p.$delay};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.7;
  }
`

export const ErrorMessage = styled.p`
  margin: 12px 0 0;
  padding: 10px 12px;
  border: 1px solid ${p => p.theme.palette.error.main};
  border-radius: 12px;
  background: ${p => p.theme.palette.colors.surfaceSoft};
  color: ${p => p.theme.palette.colors.text};
  font-family: Lato, sans-serif;
  font-size: 14px;
  line-height: 1.5;
`

export const MessageEnd = styled.div`
  width: 1px;
  height: 1px;
`

export const Composer = styled.form`
  display: flex;
  align-items: flex-end;
  gap: 10px;
  padding: 12px 14px 14px;
  border-top: 1px solid ${p => p.theme.palette.colors.borderSoft};
  background: ${p => p.theme.palette.colors.surfaceElevatedSoft};

  @media (max-width: 640px) {
    padding: 12px 14px calc(12px + env(safe-area-inset-bottom));
  }
`

export const ComposerField = styled.div`
  min-width: 0;
  flex: 1;
`

export const ComposerTextarea = styled.textarea`
  width: 100%;
  min-height: 50px;
  max-height: 118px;
  display: block;
  resize: none;
  padding: 12px 14px;
  border: 1px solid ${p => p.theme.palette.colors.inputBorder};
  border-radius: 15px;
  outline: none;
  background: ${p => p.theme.palette.colors.inputSurface};
  color: ${p => p.theme.palette.colors.text};
  font-family: Lato, sans-serif;
  font-size: 16px;
  line-height: 1.5;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder {
    color: ${p => p.theme.palette.colors.subdued};
    opacity: 1;
  }

  &:focus {
    border-color: ${p => p.theme.palette.blue.main};
    box-shadow: 0 0 0 4px ${p => p.theme.palette.colors.badgeMuted};
  }

  &:disabled {
    color: ${p => p.theme.palette.colors.disabledText};
    cursor: not-allowed;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const CharacterCount = styled.span`
  display: block;
  margin: 4px 4px 0 0;
  color: ${p => p.theme.palette.colors.subdued};
  font-family: Poppins, sans-serif;
  font-size: 10px;
  line-height: 1;
  text-align: right;
`

export const SendButton = styled.button`
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  display: grid;
  place-items: center;
  padding: 0;
  border: 0;
  border-radius: 15px;
  background: ${p => p.theme.palette.blue.main};
  cursor: pointer;
  transition: transform 0.2s ease, opacity 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 2px solid ${p => p.theme.palette.blue.main};
    outline-offset: 3px;
  }

  &:disabled {
    background: ${p => p.theme.palette.colors.disabledBackground};
    cursor: not-allowed;
    opacity: 0.8;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const SendIcon = styled(Icon)`
  width: 20px;
  height: 20px;
  margin: 0;
  filter: brightness(0) invert(1);
`

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

export const panelFocusStyles = css`
  &:focus-visible {
    outline: 2px solid ${p => p.theme.palette.blue.main};
    outline-offset: 2px;
  }
`
